/**
 * Impulsionar: a tabela objetivo → Meta, o montador de público e as
 * funções puras que cliente e servidor compartilham.
 *
 * Tudo que é "contrato da Meta" (objective, optimization_goal,
 * destination_type, promoted_object, call_to_action) mora AQUI, num lugar só.
 * Quando um objetivo for recusado numa conta real, é esta tabela que se
 * ajusta — não o assistente, não o orquestrador.
 */

export type ObjetivoImpulsionar = "whatsapp" | "direct" | "engajamento" | "trafego";

export type DefinicaoObjetivo = {
  label: string;
  descricao: string;
  objective: "OUTCOME_ENGAGEMENT" | "OUTCOME_TRAFFIC";
  optimizationGoal: "CONVERSATIONS" | "POST_ENGAGEMENT" | "LINK_CLICKS";
  billingEvent: "IMPRESSIONS";
  destinationType: "WHATSAPP" | "INSTAGRAM_DIRECT" | "ON_POST" | "WEBSITE";
  /** Precisa de um link de destino (tráfego). */
  precisaLink: boolean;
  /** A Página precisa ter WhatsApp vinculado. */
  precisaWhatsAppNaPagina: boolean;
  /** Rótulo do botão que a pessoa vê no anúncio. */
  ctaLabel: string | null;
};

export const OBJETIVOS_IMPULSIONAR: Record<ObjetivoImpulsionar, DefinicaoObjetivo> = {
  whatsapp: {
    label: "Mensagens no WhatsApp",
    descricao: "Quem toca no anúncio abre uma conversa com o seu número oficial. Bom para vender e tirar dúvidas.",
    objective: "OUTCOME_ENGAGEMENT",
    optimizationGoal: "CONVERSATIONS",
    billingEvent: "IMPRESSIONS",
    destinationType: "WHATSAPP",
    precisaLink: false,
    precisaWhatsAppNaPagina: true,
    ctaLabel: "Enviar mensagem no WhatsApp",
  },
  direct: {
    label: "Mensagens no Direct",
    descricao: "Quem toca no anúncio abre uma DM no Instagram — cai no Atendimento e nas automações.",
    objective: "OUTCOME_ENGAGEMENT",
    optimizationGoal: "CONVERSATIONS",
    billingEvent: "IMPRESSIONS",
    destinationType: "INSTAGRAM_DIRECT",
    precisaLink: false,
    precisaWhatsAppNaPagina: false,
    ctaLabel: "Enviar mensagem",
  },
  engajamento: {
    label: "Engajamento no post",
    descricao: "Mais curtidas, comentários e salvamentos — combina com as automações de comentário.",
    objective: "OUTCOME_ENGAGEMENT",
    optimizationGoal: "POST_ENGAGEMENT",
    billingEvent: "IMPRESSIONS",
    destinationType: "ON_POST",
    precisaLink: false,
    precisaWhatsAppNaPagina: false,
    ctaLabel: null,
  },
  trafego: {
    label: "Tráfego para um link",
    descricao: "Leva a pessoa para uma página sua (inscrição, catálogo, site).",
    objective: "OUTCOME_TRAFFIC",
    optimizationGoal: "LINK_CLICKS",
    billingEvent: "IMPRESSIONS",
    destinationType: "WEBSITE",
    precisaLink: true,
    precisaWhatsAppNaPagina: false,
    ctaLabel: "Saiba mais",
  },
};

export const OBJETIVO_KEYS = Object.keys(OBJETIVOS_IMPULSIONAR) as ObjetivoImpulsionar[];

/** `promoted_object` do conjunto. `undefined` = não mandar o campo. */
export function montarPromotedObject(
  key: ObjetivoImpulsionar,
  ctx: { pageId: string; whatsappPhoneNumber?: string | null },
): Record<string, unknown> | undefined {
  switch (key) {
    case "whatsapp":
      return {
        page_id: ctx.pageId,
        ...(ctx.whatsappPhoneNumber ? { whatsapp_phone_number: ctx.whatsappPhoneNumber } : {}),
      };
    case "direct":
      return { page_id: ctx.pageId };
    case "engajamento":
    case "trafego":
      return undefined;
  }
}

/** `call_to_action` do criativo. `undefined` = sem botão. */
export function montarCallToAction(
  key: ObjetivoImpulsionar,
  ctx: { link?: string | null },
): { type: string; value: Record<string, unknown> } | undefined {
  switch (key) {
    case "whatsapp":
      return { type: "WHATSAPP_MESSAGE", value: { app_destination: "WHATSAPP", link: "https://api.whatsapp.com/send" } };
    case "direct":
      return { type: "INSTAGRAM_MESSAGE", value: { app_destination: "INSTAGRAM_DIRECT" } };
    case "trafego":
      return ctx.link ? { type: "LEARN_MORE", value: { link: ctx.link } } : undefined;
    case "engajamento":
      return undefined;
  }
}

// ─── Público ─────────────────────────────────────────────────────────────────

export type LocalPublico = {
  key: string;
  name: string;
  type: "country" | "region" | "city" | string;
  countryCode?: string | null;
};

export type InteressePublico = { id: string; name: string };

export type PublicoForm = {
  locais: LocalPublico[];
  idadeMin: number;
  idadeMax: number;
  genero: "all" | "male" | "female";
  interesses: InteressePublico[];
  advantagePlus: boolean;
};

export const PUBLICO_PADRAO: PublicoForm = {
  locais: [{ key: "BR", name: "Brasil", type: "country", countryCode: "BR" }],
  idadeMin: 18,
  idadeMax: 65,
  genero: "all",
  interesses: [],
  advantagePlus: true,
};

export const IDADE_MIN = 18;
/** 65 na Meta significa "65+". */
export const IDADE_MAX = 65;

/**
 * Monta o `targeting` no formato da Meta.
 *
 * `targeting_automation.advantage_audience` vai SEMPRE (0 ou 1): desde a
 * v23 a Meta recusa conjunto com idade/gênero/interesses restritos sem esse
 * campo explícito. Posicionamentos ficam automáticos (sem
 * `publisher_platforms`), que é o que a Meta recomenda e o que dá mais
 * entrega por real.
 */
export function montarTargeting(p: PublicoForm): Record<string, unknown> {
  const countries = p.locais.filter((l) => l.type === "country").map((l) => l.key);
  const regions = p.locais.filter((l) => l.type === "region").map((l) => ({ key: l.key }));
  const cities = p.locais.filter((l) => l.type === "city").map((l) => ({ key: l.key }));
  const geo: Record<string, unknown> = {};
  if (countries.length) geo.countries = countries;
  if (regions.length) geo.regions = regions;
  if (cities.length) geo.cities = cities;

  const targeting: Record<string, unknown> = {
    geo_locations: geo,
    age_min: Math.max(IDADE_MIN, Math.min(p.idadeMin, IDADE_MAX)),
    age_max: Math.max(IDADE_MIN, Math.min(p.idadeMax, IDADE_MAX)),
    targeting_automation: { advantage_audience: p.advantagePlus ? 1 : 0 },
  };
  if (p.genero === "male") targeting.genders = [1];
  if (p.genero === "female") targeting.genders = [2];
  if (p.interesses.length > 0) {
    targeting.flexible_spec = [{ interests: p.interesses.map((i) => ({ id: i.id, name: i.name })) }];
  }
  return targeting;
}

export function publicoValido(p: PublicoForm): string | null {
  if (p.locais.length === 0) return "Escolha ao menos um lugar.";
  if (p.idadeMin > p.idadeMax) return "A idade mínima não pode passar da máxima.";
  return null;
}

// ─── Nome e estimativa ───────────────────────────────────────────────────────

export function nomeDoImpulsionamento(legenda: string | null | undefined, data: Date): string {
  const limpa = (legenda ?? "").replace(/\s+/g, " ").trim();
  const trecho = limpa.length > 40 ? `${limpa.slice(0, 40).trimEnd()}…` : limpa;
  const dia = data.toLocaleDateString("pt-BR");
  return trecho ? `Impulsionar: ${trecho} — ${dia}` : `Impulsionar: post — ${dia}`;
}

export function estimativaGasto(
  dailyCents: number,
  inicio: Date,
  fim: Date | null,
): { dias: number | null; totalCents: number | null } {
  if (!fim) return { dias: null, totalCents: null };
  const dias = Math.max(1, Math.ceil((fim.getTime() - inicio.getTime()) / 86_400_000));
  return { dias, totalCents: dias * dailyCents };
}
